export type InsurancePolicyResult = {
  success?: boolean
  unchanged?: boolean
  msg?: string
  [key: string]: unknown
}

export type InsurancePolicyOutcome = {
  committed: boolean
  unchanged: boolean
  failed?: InsurancePolicyResult
}

export type StoredInsuranceCoverage = {
  depart: string
  returnDate: string
  cost: number
  policies?: Array<{
    policyNumber: string
    depart: string
    returnDate: string
    cost: number
  }>
}

/**
 * CommitPolicyChanges groups responses by Arch order number, while cancellation
 * responses are returned directly. Present both shapes as one result list so
 * callers do not mistake a successful keyed response for a failure.
 */
export function normalizeInsurancePolicyResults(value: unknown): InsurancePolicyResult[] {
  if (Array.isArray(value)) {
    return value.flatMap(normalizeInsurancePolicyResults)
  }

  if (value == null || typeof value !== 'object') {
    return []
  }

  const result = value as InsurancePolicyResult
  if (typeof result.success === 'boolean') {
    return [result]
  }

  return Object.values(result).flatMap(normalizeInsurancePolicyResults)
}

export function classifyInsurancePolicyResults(results: InsurancePolicyResult[]): InsurancePolicyOutcome {
  const committed = results.some((result) => result?.success === true && result.unchanged !== true)
  const unchanged = results.length > 0
    && results.every((result) => result?.success === true && result.unchanged === true)
  const failed = results.find((result) => !result || result.success !== true)

  return { committed, unchanged, failed }
}

function dateOnly(value: unknown): string {
  return value == null ? '' : String(value).slice(0, 10)
}

/**
 * Resolve the coverage that is already committed at Arch. Purchase responses
 * expose coverageDetail, while policy-change responses expose policyResponses.
 * Both are stronger evidence than a previously submitted quote or purchase.
 */
export function getLatestCommittedInsuranceCoverage(
  newest: any,
  namesRow: any
): StoredInsuranceCoverage {
  const summaries = Array.isArray(newest?.raw_response?.policyResponses)
    ? newest.raw_response.policyResponses
      .map((response: any) => response?.policySummary)
      .filter(Boolean)
    : []
  const coverageDetails = Array.isArray(newest?.raw_response?.coverageDetail)
    ? newest.raw_response.coverageDetail.filter(Boolean)
    : []
  const updates = newest?.raw_quote_info?.updates

  if (summaries.length > 0) {
    return {
      depart: dateOnly(summaries[0]?.departureDate ?? summaries[0]?.effectiveDate),
      returnDate: dateOnly(summaries[0]?.returnDate ?? summaries[0]?.expirationDate),
      cost: summaries.reduce((sum: number, policy: any) => sum + (parseFloat(policy?.tripCost) || 0), 0)
    }
  }

  if (coverageDetails.length > 0) {
    return {
      depart: dateOnly(coverageDetails[0]?.departureDate ?? coverageDetails[0]?.effectiveDate),
      returnDate: dateOnly(coverageDetails[0]?.returnDate ?? coverageDetails[0]?.expirationDate),
      cost: coverageDetails.reduce((sum: number, policy: any) => sum + (parseFloat(policy?.tripCost) || 0), 0)
    }
  }

  if (Array.isArray(updates) && updates.length > 0) {
    return {
      depart: dateOnly(updates[0]?.coverage?.effectiveDate),
      returnDate: dateOnly(updates[0]?.coverage?.expirationDate),
      cost: updates.reduce(
        (sum: number, update: any) => sum + (parseFloat(update?.coverage?.totalTripCost) || 0),
        0
      )
    }
  }

  const purchase = namesRow?.raw_purchase || {}
  return {
    depart: dateOnly(purchase.coverage?.effectiveDate),
    returnDate: dateOnly(purchase.coverage?.expirationDate),
    cost: (Array.isArray(purchase.insuredPerson) ? purchase.insuredPerson : [])
      .reduce((sum: number, person: any) => sum + (parseFloat(person?.individualTripCost) || 0), 0)
  }
}

/**
 * A successful policy-change row only contains the policies changed by that request. Rebuild the
 * complete Arch order by overlaying every committed revision on the original coverageDetail.
 */
export function getMergedCommittedInsuranceCoverage(
  rows: any[],
  namesRow: any
): StoredInsuranceCoverage {
  const policies = new Map<string, any>()
  const orderedRows = (Array.isArray(rows) ? rows : [])
    .slice()
    .sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0))

  const overlay = (detail: any) => {
    const policyNumber = String(detail?.policyNumber ?? '').trim()
    if (!policyNumber) return
    policies.set(policyNumber, { ...(policies.get(policyNumber) ?? {}), ...detail })
  }

  for (const row of orderedRows) {
    const coverageDetails = Array.isArray(row?.raw_response?.coverageDetail)
      ? row.raw_response.coverageDetail
      : []
    coverageDetails.forEach(overlay)

    const policyResponses = Array.isArray(row?.raw_response?.policyResponses)
      ? row.raw_response.policyResponses
      : []
    policyResponses.map((response: any) => response?.policySummary).filter(Boolean).forEach(overlay)

    // A status=1 legacy revision may contain committed update details without policyResponses.
    if (coverageDetails.length === 0 && policyResponses.length === 0) {
      const updates = Array.isArray(row?.raw_quote_info?.updates) ? row.raw_quote_info.updates : []
      updates.forEach((update: any) => overlay({
        policyNumber: update?.policyNumber,
        departureDate: update?.coverage?.effectiveDate,
        returnDate: update?.coverage?.expirationDate,
        tripCost: update?.coverage?.totalTripCost
      }))
    }
  }

  if (policies.size === 0) {
    return getLatestCommittedInsuranceCoverage(orderedRows[orderedRows.length - 1], namesRow)
  }

  const policyList = Array.from(policies.entries()).map(([policyNumber, detail]) => ({
    policyNumber,
    depart: dateOnly(detail?.departureDate ?? detail?.effectiveDate),
    returnDate: dateOnly(detail?.returnDate ?? detail?.expirationDate),
    cost: parseFloat(detail?.tripCost ?? detail?.totalTripCost) || 0
  }))

  return {
    depart: policyList[0]?.depart ?? '',
    returnDate: policyList[0]?.returnDate ?? '',
    cost: policyList.reduce((sum, policy) => sum + policy.cost, 0),
    policies: policyList
  }
}
